import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
  AccessTime,
  Store,
} from '@mui/icons-material';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
} from 'date-fns';
import EmployeeLayout from '../components/layout/EmployeeLayout';
import { useAuth } from '../context/AuthContext';
import { useEmployeeSchedules, EmployeeShift } from '../hooks/useEmployeeSchedules';
import { useEmployees } from '../hooks/useEmployees';
import { useShops } from '../hooks/useShops';

export default function EmployeeSchedulePage() {
  const { user } = useAuth();
  const { employees } = useEmployees();
  const { shops } = useShops();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Find the current employee record
  const currentEmployee = useMemo(() => {
    if (!user?.employeeId) return null;
    return employees.find(emp => emp.id === user.employeeId);
  }, [user?.employeeId, employees]);

  // Get schedules for this month (with buffer for week view)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const {
    schedules,
    isLoading,
  } = useEmployeeSchedules(
    user?.employeeId,
    format(calendarStart, 'yyyy-MM-dd'),
    format(calendarEnd, 'yyyy-MM-dd')
  );

  // Build calendar days
  const calendarDays = useMemo(() => {
    const days: Array<{
      date: Date;
      isCurrentMonth: boolean;
      isToday: boolean;
      shifts: EmployeeShift[];
    }> = [];

    let day = calendarStart;
    const today = new Date();

    while (day <= calendarEnd) {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayShifts = schedules.filter(s => s.date === dateStr);

      days.push({
        date: new Date(day),
        isCurrentMonth: isSameMonth(day, currentMonth),
        isToday: isSameDay(day, today),
        shifts: dayShifts,
      });

      day = addDays(day, 1);
    }

    return days;
  }, [calendarStart, calendarEnd, schedules, currentMonth]);

  // Get shop by ID
  const getShop = (shopId: string) => {
    return shops.find(s => s.id === shopId);
  };

  // Get shop name by ID
  const getShopName = (shopId: string) => {
    const shop = getShop(shopId);
    return shop?.name || 'Unknown Shop';
  };

  // Get contrasting text color (black or white) based on background color
  const getContrastColor = (hexColor: string): string => {
    // Remove # if present
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Calculate monthly stats
  const monthStats = useMemo(() => {
    const monthShifts = schedules.filter(s => {
      const shiftDate = parseISO(s.date);
      return isSameMonth(shiftDate, currentMonth);
    });

    const totalHours = monthShifts.reduce((acc, shift) => {
      if (shift.startTime && shift.endTime) {
        const [startH, startM] = shift.startTime.split(':').map(Number);
        const [endH, endM] = shift.endTime.split(':').map(Number);
        let hours = endH - startH + (endM - startM) / 60;
        if (hours < 0) hours += 24;
        return acc + hours;
      }
      return acc;
    }, 0);

    return {
      shifts: monthShifts.length,
      totalHours: Math.round(totalHours * 10) / 10,
    };
  }, [schedules, currentMonth]);

  if (!currentEmployee && !isLoading) {
    return (
      <EmployeeLayout pageTitle="My Schedule">
        <Alert severity="warning" sx={{ mt: 2 }}>
          Your employee profile could not be found. Please contact your manager.
        </Alert>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout pageTitle="My Schedule">
      {/* Header */}
      <Box
        sx={{
          mb: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        <Typography
          variant="h4"
          fontWeight={600}
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          My Schedule
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={<CalendarMonth sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            label={`${monthStats.shifts} shifts`}
            color="primary"
            variant="outlined"
            size="small"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
          />
          <Chip
            icon={<AccessTime sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            label={`${monthStats.totalHours}h total`}
            color="success"
            variant="outlined"
            size="small"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
          />
        </Box>
      </Box>

      {/* Month Navigation */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <IconButton
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            size="small"
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
          >
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <IconButton
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            size="small"
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2, overflow: 'hidden' }}>
          {/* Scrollable container for mobile */}
          <Box
            sx={{
              overflowX: 'auto',
              pb: 1,
              // Smooth scrolling on mobile
              WebkitOverflowScrolling: 'touch',
              // Hide scrollbar on desktop but allow on mobile
              '&::-webkit-scrollbar': {
                height: { xs: 4, sm: 0 },
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'divider',
                borderRadius: 2,
              },
            }}
          >
            <Box sx={{ minWidth: { xs: 500, sm: 'auto' } }}>
              {/* Week days header */}
              <Grid container spacing={{ xs: 0.5, sm: 1 }} sx={{ mb: 1 }}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <Grid item xs={12 / 7} key={day}>
                    <Typography
                      variant="subtitle2"
                      align="center"
                      color="text.secondary"
                      fontWeight={600}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                    >
                      {day}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar grid */}
              <Grid container spacing={{ xs: 0.5, sm: 1 }}>
                {calendarDays.map(({ date, isCurrentMonth, isToday, shifts }) => (
                  <Grid item xs={12 / 7} key={date.toISOString()}>
                    <Card
                      variant="outlined"
                      sx={{
                        minHeight: { xs: 70, sm: 100 },
                        opacity: isCurrentMonth ? 1 : 0.5,
                        borderColor: isToday ? 'primary.main' : 'divider',
                        borderWidth: isToday ? 2 : 1,
                        bgcolor: shifts.length > 0 ? 'action.hover' : 'transparent',
                      }}
                    >
                      <CardContent sx={{ p: { xs: 0.5, sm: 1 }, '&:last-child': { pb: { xs: 0.5, sm: 1 } } }}>
                        <Typography
                          variant="body2"
                          fontWeight={isToday ? 700 : 500}
                          color={isToday ? 'primary.main' : 'text.primary'}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          {format(date, 'd')}
                        </Typography>
                        {shifts.map((shift, idx) => {
                          const shop = getShop(shift.shopId);
                          const shopColor = shop?.color || '#1976d2';
                          const textColor = getContrastColor(shopColor);
                          const shopName = getShopName(shift.shopId);
                          return (
                            <Box key={`${shift.scheduleId}-${idx}`} sx={{ mt: 0.5 }}>
                              <Chip
                                icon={<Store sx={{ fontSize: { xs: 10, sm: 12 }, color: `${textColor} !important` }} />}
                                label={shopName}
                                size="small"
                                sx={{
                                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                                  height: { xs: 16, sm: 20 },
                                  width: '100%',
                                  bgcolor: shopColor,
                                  color: textColor,
                                  '& .MuiChip-label': {
                                    px: 0.5,
                                  },
                                  '& .MuiChip-icon': {
                                    ml: { xs: 0.25, sm: 0.5 },
                                  },
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  textAlign: 'center',
                                  fontSize: { xs: '0.55rem', sm: '0.65rem' },
                                  color: 'text.secondary',
                                  mt: 0.25,
                                }}
                              >
                                {shift.startTime} - {shift.endTime}
                              </Typography>
                            </Box>
                          );
                        })}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* Mobile scroll hint */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: { xs: 'block', sm: 'none' },
              textAlign: 'center',
              mt: 1,
              fontStyle: 'italic',
            }}
          >
            Swipe to see full calendar
          </Typography>
        </Paper>
      )}
    </EmployeeLayout>
  );
}
